import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Gauge, LockKey, Phone, Megaphone, Warning, Info, UserPlus, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "../lib/api";

function AnnouncementBanner({ items, speed = 6 }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!items || items.length <= 1) return;
    const dwell = Math.max(3, Number(speed) || 6) * 1000;
    const fade = 500;
    const int = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, fade);
    }, dwell);
    return () => clearInterval(int);
  }, [items, speed]);
  if (!items || items.length === 0) return null;
  const current = items[idx] || items[0];
  const bgFor = (sev) => {
    if (sev === "critical") return "bg-red-600 text-white";
    if (sev === "warning") return "bg-amber-500 text-white";
    return "bg-emerald-600 text-white";
  };
  const iconFor = (sev) => sev === "critical" ? <Warning weight="fill" size={16}/>
    : sev === "warning" ? <Warning size={16}/>
    : <Info weight="fill" size={16}/>;
  return (
    <div className={`w-full transition-colors duration-500 ${bgFor(current.severity)}`} data-testid="login-announcements">
      <div
        key={current.id}
        className={`w-full py-2 px-4 text-center text-sm font-semibold flex items-center justify-center gap-2 transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
      >
        {iconFor(current.severity)}
        <span>{current.message}</span>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phones, setPhones] = useState({ support_phone: "", virtual_assistant_phone: "" });
  const [announcements, setAnnouncements] = useState([]);
  const [announceSpeed, setAnnounceSpeed] = useState(30);
  const [remoteAssistUrl, setRemoteAssistUrl] = useState("");
  const [signupOpen, setSignupOpen] = useState(false);
  const [signup, setSignup] = useState({ full_name: "", email: "", company: "", phone: "", password: "" });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [newPwd, setNewPwd] = useState({ p1: "", p2: "" });
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState("");

  const doSignup = async (e) => {
    e?.preventDefault?.();
    if (!signup.email || !signup.password || !signup.full_name) {
      toast.error("Nom, email et mot de passe requis"); return;
    }
    if (signup.password.length < 6) {
      toast.error("Mot de passe : 6 caractères minimum"); return;
    }
    setBusy(true);
    try {
      await api.post("/auth/register", signup);
      toast.success("Compte créé — un email d'activation vient de vous être envoyé", { duration: 6000 });
      setSignupOpen(false);
      setSignup({ full_name: "", email: "", company: "", phone: "", password: "" });
      setPendingVerifyEmail(signup.email);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de créer le compte");
    } finally { setBusy(false); }
  };

  // Email verification link: /login?verify=<token>
  useEffect(() => {
    const t = searchParams.get("verify");
    if (!t) return;
    (async () => {
      setBusy(true);
      try {
        const r = await api.post("/auth/verify-email", { token: t });
        localStorage.setItem("helios_token", r.data.access_token);
        localStorage.setItem("helios_user", JSON.stringify(r.data.user));
        toast.success(`Compte activé — bienvenue ${r.data.user.full_name}`);
        setTimeout(() => { window.location.href = "/mes-tickets"; }, 700);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Lien de validation expiré ou invalide");
      } finally { setBusy(false); }
    })();
  }, []);

  // Password reset link: /login?reset=<token>
  useEffect(() => {
    const t = searchParams.get("reset");
    if (t) setResetToken(t);
  }, [searchParams]);

  const requestReset = async (e) => {
    e?.preventDefault?.();
    if (!forgotEmail.includes("@")) { toast.error("Email invalide"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email: forgotEmail.toLowerCase().trim() });
      toast.success("Si un compte existe pour cet email, un lien vient d'être envoyé");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const confirmReset = async (e) => {
    e?.preventDefault?.();
    if (newPwd.p1.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
    if (newPwd.p1 !== newPwd.p2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/password-reset/confirm", { token: resetToken, password: newPwd.p1 });
      localStorage.setItem("helios_token", r.data.access_token);
      localStorage.setItem("helios_user", JSON.stringify(r.data.user));
      toast.success("Mot de passe modifié — vous êtes connecté(e)");
      const dest = r.data.user.role === "client" ? "/mes-tickets" : "/dashboard";
      window.location.href = dest;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lien expiré ou invalide");
    } finally { setBusy(false); }
  };

  // Magic-link auto-login: /login?magic=<jwt>&next=/tickets/<id>
  useEffect(() => {
    const magic = searchParams.get("magic");
    if (!magic) return;
    (async () => {
      try {
        const r = await api.post("/auth/magic-login", { token: magic });
        localStorage.setItem("helios_token", r.data.access_token);
        localStorage.setItem("helios_user", JSON.stringify(r.data.user));
        toast.success(`Bienvenue ${r.data.user.full_name}`);
        const next = searchParams.get("next") || (r.data.user.role === "client" ? "/mes-tickets" : "/dashboard");
        window.location.href = next;
      } catch (e) {
        toast.error("Lien expiré ou invalide — veuillez vous reconnecter");
      }
    })();
  }, [searchParams]);

  useEffect(() => {
    if (user) nav(user.role === "client" ? "/mes-tickets" : "/dashboard");
  }, [user, nav]);

  useEffect(() => {
    api.get(`/settings/portal/public?t=${Date.now()}`).then((r) => {
      setPhones({
        support_phone: r.data?.support_phone || "",
        virtual_assistant_phone: r.data?.virtual_assistant_phone || "",
      });
      setAnnouncements(r.data?.announcements || []);
      setAnnounceSpeed(r.data?.announcement_speed_seconds || 30);
      setRemoteAssistUrl(r.data?.remote_assist_url || "");
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password, totp);
      toast.success(`Bienvenue ${u.full_name}`);
      nav(u.role === "client" ? "/mes-tickets" : "/dashboard");
    } catch (err) {
      if (err.response?.status === 428) {
        setNeed2fa(true);
        toast.info("Code 2FA requis");
      } else {
        toast.error(err.response?.data?.detail || "Connexion impossible");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <AnnouncementBanner items={announcements} speed={announceSpeed}/>
      <div className="flex-1 grid md:grid-cols-2">
      <div className="hidden md:flex relative bg-[hsl(222_35%_9%)] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          background: "radial-gradient(600px circle at 20% 30%, rgba(37,99,235,0.35), transparent 40%), radial-gradient(400px circle at 80% 70%, rgba(255,120,50,0.25), transparent 40%)"
        }}/>
        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Gauge weight="bold" size={22}/>
          </div>
          <div>
            <div className="font-display text-xl font-bold">MasterTicket</div>
            <div className="text-xs text-white/60 uppercase tracking-widest">Support IT</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
            Une seule console.<br/>
            <span className="text-blue-400">Tout votre support.</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-md">
            Formulaire web, e-mails, base de connaissances, SLA, IA — centralisez tous vos tickets d&apos;assistance informatique.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            <Stat label="Priorités" value="4 niveaux"/>
            <Stat label="Statuts" value="Kanban complet"/>
            <Stat label="Sources" value="Web + Email"/>
            <Stat label="IA" value="Claude Sonnet"/>
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} — MasterTicket Ticketing · créé par <a href="https://www.netkom.com" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 underline underline-offset-2">NetKom Informatique</a></div>
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-lg border-none shadow-none md:shadow-md">
          <CardContent className="pt-8 pb-8 px-8">
            <div className="mb-8">
              <div className="label-caps">Connexion</div>
              <h2 className="font-display text-3xl font-bold mt-1">Accéder à la console</h2>
              <p className="text-sm text-muted-foreground mt-2">Techniciens, administrateurs et clients.</p>
              {(phones.support_phone || phones.virtual_assistant_phone || remoteAssistUrl) && (
                <div className="mt-4 flex items-start gap-3 text-xs">
                  <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                    {phones.support_phone && (
                      <a href={`tel:${phones.support_phone.replace(/\s+/g, "")}`}
                         data-testid="login-phone-support"
                         className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 hover:underline">
                        <Phone weight="fill" size={13}/>
                        <span className="text-muted-foreground">Ligne support :</span>
                        <span className="mono font-medium">{phones.support_phone}</span>
                      </a>
                    )}
                    {phones.virtual_assistant_phone && (
                      <a href={`tel:${phones.virtual_assistant_phone.replace(/\s+/g, "")}`}
                         data-testid="login-phone-virtual"
                         className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 hover:underline">
                        <Phone weight="fill" size={13}/>
                        <span className="text-muted-foreground">Assistant Virtuel 24h/24 :</span>
                        <span className="mono font-medium">{phones.virtual_assistant_phone}</span>
                      </a>
                    )}
                  </div>
                  {remoteAssistUrl && (
                    <a href={remoteAssistUrl} target="_blank" rel="noopener noreferrer"
                       data-testid="login-remote-assist"
                       title="Télémaintenance"
                       className="shrink-0 h-[36px] w-[36px] rounded-full overflow-hidden ring-2 ring-blue-500/40 hover:ring-blue-500 hover:scale-105 transition-all shadow-md">
                      <img src="https://www.netkom.com/img/aide.jpg" alt="Télémaintenance" className="w-full h-full object-cover"/>
                    </a>
                  )}
                </div>
              )}
            </div>
            <form onSubmit={submit} className="space-y-4">
              {pendingVerifyEmail && (
                <div className="rounded-md border border-blue-300 bg-blue-50 dark:bg-blue-950/20 p-3 text-sm text-blue-900 dark:text-blue-200" data-testid="verify-pending-banner">
                  <b>Vérifiez votre boîte mail</b> — un lien d&apos;activation vient d&apos;être envoyé à <span className="mono">{pendingVerifyEmail}</span>. Cliquez dessus pour activer votre compte avant de vous connecter.
                </div>
              )}
              <div>
                <Label htmlFor="email">Adresse e-mail</Label>
                <Input id="email" data-testid="login-email"
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@entreprise.fr" />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" data-testid="login-password"
                  type="password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" />
              </div>
              {need2fa && (
                <div>
                  <Label htmlFor="totp">Code d&apos;authentification (Google Authenticator)</Label>
                  <Input id="totp" data-testid="login-totp"
                    inputMode="numeric" maxLength={6} required
                    value={totp} onChange={(e) => setTotp(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="123456"/>
                </div>
              )}
              <Button data-testid="login-submit" type="submit" className="w-full" disabled={busy}>
                <LockKey size={16} className="mr-2"/>
                {busy ? "Connexion…" : "Se connecter"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => setForgotOpen(true)}
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setSignupOpen(true)}
                  data-testid="signup-link"
                >
                  <UserPlus size={16} className="mr-2"/>
                  Inscription
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => { window.location.href = "/portail"; }}
                  data-testid="portal-link"
                >
                  <Megaphone size={16} className="mr-2"/>
                  Ouvrir un ticket
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      </div>

      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-md" data-testid="signup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <UserPlus size={20}/> Créer un compte client
            </DialogTitle>
            <DialogDescription>
              Créez votre espace pour gérer vos tickets en ligne et suivre l&apos;avancement de vos demandes.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={doSignup} className="space-y-3 pt-2">
            <div>
              <Label>Nom complet *</Label>
              <Input required value={signup.full_name}
                onChange={(e) => setSignup({...signup, full_name: e.target.value})}
                placeholder="Frédéric Soubité" data-testid="signup-name"/>
            </div>
            <div>
              <Label>Email *</Label>
              <Input required type="email" value={signup.email}
                onChange={(e) => setSignup({...signup, email: e.target.value})}
                placeholder="vous@societe.fr" data-testid="signup-email"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Société</Label>
                <Input value={signup.company}
                  onChange={(e) => setSignup({...signup, company: e.target.value})}
                  placeholder="Netkom" data-testid="signup-company"/>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={signup.phone}
                  onChange={(e) => setSignup({...signup, phone: e.target.value})}
                  placeholder="04 74 12 34 56" data-testid="signup-phone"/>
              </div>
            </div>
            <div>
              <Label>Mot de passe *</Label>
              <Input required type="password" minLength={6} value={signup.password}
                onChange={(e) => setSignup({...signup, password: e.target.value})}
                placeholder="Au moins 6 caractères" data-testid="signup-password"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setSignupOpen(false)} disabled={busy}>Annuler</Button>
              <Button type="submit" disabled={busy} data-testid="signup-submit">
                {busy ? "Création…" : "Créer mon compte"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot password dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-sm" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <LockKey size={20}/> Mot de passe oublié
            </DialogTitle>
            <DialogDescription>
              Saisissez votre adresse e-mail. Si un compte existe, nous vous envoyons un lien pour choisir un nouveau mot de passe (valide 1 heure).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={requestReset} className="space-y-3 pt-2">
            <div>
              <Label>Adresse e-mail *</Label>
              <Input required type="email" autoFocus autoComplete="email"
                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="vous@entreprise.fr" data-testid="forgot-email"/>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setForgotOpen(false)} disabled={busy}>Annuler</Button>
              <Button type="submit" disabled={busy} data-testid="forgot-submit">
                {busy ? "Envoi…" : "Envoyer le lien"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog (opens when ?reset=<token> is in URL) */}
      <Dialog open={!!resetToken} onOpenChange={(v) => !v && setResetToken(null)}>
        <DialogContent className="max-w-sm" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <LockKey size={20}/> Nouveau mot de passe
            </DialogTitle>
            <DialogDescription>
              Choisissez un mot de passe robuste (8 caractères minimum).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={confirmReset} className="space-y-3 pt-2">
            <div>
              <Label>Nouveau mot de passe *</Label>
              <Input required type="password" autoFocus autoComplete="new-password" minLength={8}
                value={newPwd.p1} onChange={(e) => setNewPwd({...newPwd, p1: e.target.value})}
                placeholder="••••••••" data-testid="reset-p1"/>
            </div>
            <div>
              <Label>Confirmer *</Label>
              <Input required type="password" autoComplete="new-password" minLength={8}
                value={newPwd.p2} onChange={(e) => setNewPwd({...newPwd, p2: e.target.value})}
                placeholder="••••••••" data-testid="reset-p2"/>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={() => setResetToken(null)} disabled={busy}>Annuler</Button>
              <Button type="submit" disabled={busy} data-testid="reset-submit">
                {busy ? "Enregistrement…" : "Valider"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-4">
      <div className="text-[0.65rem] uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-1 font-display font-semibold">{value}</div>
    </div>
  );
}
